import Phaser from 'phaser';
import { t } from '@i18n/index';
import { PALETTE, TEXT_COLOUR, FONT, readoutStyle, px } from '../theme';
import { ADVISER_PORTRAIT_ID } from '@core/character/character';
import type { Campaign } from '@core/campaign/campaign';
import {
  pendingStep,
  markSeen,
  catchUpTo,
  isComplete,
  stepTextKey,
  hintTextKey,
  type TutorialStep,
  type HintTopic,
} from '@core/tutorial/tutorial';
import { portraitKey } from '../assets';
import { drawFallbackPortrait } from '../render/portrait';
import { audio } from '../audio/engine';

/**
 * Dr. Hedda Roskopf meldet sich.
 *
 * Eine Einblendung am unteren Rand mit ihrem Portraet und einem Satz oder
 * dreien. Zwei Dinge sind daran wichtig:
 *
 *  * **Sie blockiert nicht.** Die Einblendung liegt ueber der Szene, nimmt
 *    aber keine Eingaben ausserhalb ihres eigenen Rahmens an. Wer sie
 *    ignoriert, spielt weiter.
 *  * **Sie geht wieder weg.** Ein Klick auf „Verstanden" schliesst sie, und
 *    derselbe Schritt kommt nicht zweimal.
 *
 * Ton: knapp, trocken, nie herablassend. Sie erklaert die Lage, nicht die
 * Bedienung — und der Witz liegt bei ihr, waehrend das Spiel ernst bleibt.
 */

export interface AdviserOptions {
  /** Der Text, den sie sagt. */
  readonly text: string;
  /** Beschriftung der Schaltflaeche. Voreinstellung: „Verstanden". */
  readonly dismissKey?: string;
  /** Laeuft nach dem Wegklicken. */
  readonly onDismiss?: () => void;
  /** Zusaetzliche Schaltflaeche, etwa zum Abschalten der Einweisung. */
  readonly secondary?: { readonly label: string; readonly onClick: () => void };
}

/** Mindesthoehe. Laengere Texte machen das Fenster hoeher, nicht enger. */
const MIN_HEIGHT = 132;
const PORTRAIT = 96;

export function showAdviser(scene: Phaser.Scene, options: AdviserOptions): Phaser.GameObjects.Container {
  const { width, height } = scene.scale;
  const panelWidth = Math.min(620, width - 48);
  const left = (width - panelWidth) / 2;

  const textLeft = 14 + PORTRAIT * 0.8 + 16;
  const textWidth = panelWidth - textLeft - 16;

  // Der Text zuerst, damit die Hoehe des Fensters aus ihm folgt. Bei fester
  // Hoehe lief die letzte Zeile bei laengeren Einwuerfen in die
  // Schaltflaechen — und mit vergroesserter Schrift bei jedem zweiten.
  const body = scene.add.text(0, 34, options.text, {
    ...readoutStyle(12),
    wordWrap: { width: textWidth },
    lineSpacing: 3,
  });

  const panelHeight = Math.max(MIN_HEIGHT, 34 + body.height + 46);
  // Ueber der Schaltflaechenreihe am unteren Rand, nicht darauf: Die Szenen
  // setzen ihre Knoepfe auf `height - 56`, und ein Fenster, das die
  // Weiter-Schaltflaeche verdeckt, waere genau das Blockieren, das hier nicht
  // passieren soll.
  const top = height - panelHeight - 104;

  body.setPosition(textLeft, 34);

  const frame = scene.add
    .rectangle(0, 0, panelWidth, panelHeight, PALETTE.panel, 0.96)
    .setOrigin(0, 0);
  frame.setStrokeStyle(1, PALETTE.panelEdge);

  const portrait = scene.add
    .image(14, 14, adviserTexture(scene))
    .setOrigin(0, 0)
    .setDisplaySize(PORTRAIT * 0.8, PORTRAIT);

  const name = scene.add.text(textLeft, 14, t('adviser.name'), {
    fontFamily: FONT.mono,
    fontSize: px(11),
    color: TEXT_COLOUR.base,
  });

  const role = scene.add.text(textLeft + name.width + 10, 15, t('adviser.role'), {
    fontFamily: FONT.mono,
    fontSize: px(10),
    color: PALETTE.inkFaint,
  });

  const container = scene.add.container(left, top, [frame, portrait, name, role, body]);
  /*
   * Ueber allem, was die Szene selbst zeichnet — sonst verschwindet die
   * Einblendung hinter einer Liste, die nach ihr entsteht.
   *
   * Die 1000 reichten dafuer nicht: Die Einsatzplanung legt bei 1500 einen
   * klickfangenden Schleier ueber die Szene. Roskopfs Einweisung zur
   * Einsatzplanung lag also darunter — abgedunkelt und, schlimmer, nicht mehr
   * wegklickbar, weil der Schleier jeden Klick abfing. Sie blieb bis zum
   * Szenenwechsel stehen. Die Einweisung ist die oberste Ebene der Oberflaeche;
   * nur die Einstellungen (2000) duerfen darueber liegen, denn die oeffnet man
   * bewusst.
   */
  container.setDepth(1900);

  const dismiss = smallButton(
    scene,
    panelWidth - 16,
    panelHeight - 14,
    t(options.dismissKey ?? 'adviser.dismiss'),
    () => {
      container.destroy();
      options.onDismiss?.();
    },
  );
  container.add(dismiss);

  if (options.secondary !== undefined) {
    const secondary = options.secondary;
    container.add(
      smallButton(scene, panelWidth - 16 - dismiss.width - 12, panelHeight - 14, secondary.label, () => {
        container.destroy();
        secondary.onClick();
      }),
    );
  }

  container.setAlpha(0);
  scene.tweens.add({ targets: container, alpha: 1, duration: 260, ease: 'Quad.easeOut' });

  return container;
}

/**
 * Zeigt den faelligen Einwurf, falls an dieser Stelle einer ansteht.
 *
 * Die Szenen rufen das am Ende ihres Aufbaus auf und muessen sich sonst um
 * nichts kuemmern: Reihenfolge, „schon gesehen" und das Abschalten liegen im
 * Kernmodul, nicht in der Darstellung.
 */
export function maybeShowTutorial(
  scene: Phaser.Scene,
  campaign: Campaign,
  at: TutorialStep,
  options: { readonly catchUp?: boolean } = {},
): void {
  // `catchUp` fuer Orte, die man zwangslaeufig erreicht: Wer am Einsatzort
  // steht, hat die Ueberfahrt hinter sich, auch wenn unterwegs nichts passiert
  // ist. Ohne das bliebe die Einweisung an einem Schritt haengen, der auf
  // kurzen Fahrten nie vorkommt.
  if (options.catchUp === true) catchUpTo(campaign.tutorial, at);
  if (pendingStep(campaign.tutorial, at) === null) return;

  markSeen(campaign.tutorial, at);
  showAdviser(scene, {
    text: t(stepTextKey(at)),
    secondary: {
      label: t('adviser.disable'),
      onClick: () => {
        campaign.tutorial.enabled = false;
      },
    },
  });
}

/**
 * Die Schaltflaeche, die sie herbeiruft.
 *
 * Sie erscheint erst, wenn die Einweisung durch ist — vorher meldet Roskopf
 * sich ohnehin von selbst, und zwei Wege zur selben Erklaerung waeren einer
 * zu viel.
 */
export function createAdviserButton(
  scene: Phaser.Scene,
  x: number,
  y: number,
  campaign: Campaign,
  topic: HintTopic,
): void {
  if (!isComplete(campaign.tutorial)) return;

  const label = scene.add
    .text(x, y, t('adviser.ask'), {
      fontFamily: FONT.mono,
      fontSize: px(11),
      color: PALETTE.inkFaint,
    })
    .setOrigin(1, 0.5)
    .setInteractive({ useHandCursor: true });

  label.on('pointerover', () => label.setColor(TEXT_COLOUR.base));
  label.on('pointerout', () => label.setColor(PALETTE.inkFaint));
  label.on('pointerdown', () => {
    audio.resume();
    showAdviser(scene, { text: t(hintTextKey(topic)) });
  });
}

/**
 * Ihr Portraet — oder ein gezeichneter Ersatz.
 *
 * Dieselbe Ueberlegung wie im Editor: Fehlende Bilddateien duerfen das Spiel
 * nicht anhalten, sie duerfen nur schlechter aussehen.
 */
function adviserTexture(scene: Phaser.Scene): string {
  const key = portraitKey(ADVISER_PORTRAIT_ID);
  if (scene.textures.exists(key)) return key;

  const fallback = `portrait-fallback:${ADVISER_PORTRAIT_ID}`;
  if (!scene.textures.exists(fallback)) {
    scene.textures.addCanvas(fallback, drawFallbackPortrait(ADVISER_PORTRAIT_ID, 128));
  }
  return fallback;
}

/**
 * Eine schmale Schaltflaeche, rechtsbuendig gesetzt.
 *
 * Nicht `createButton`: Der setzt sich um seinen Mittelpunkt und in feste
 * Breiten, hier braucht es eine, die sich nach ihrem Text richtet und in einen
 * Container passt.
 */
function smallButton(
  scene: Phaser.Scene,
  right: number,
  bottom: number,
  label: string,
  onClick: () => void,
): Phaser.GameObjects.Container {
  const text = scene.add
    .text(0, 0, label, { fontFamily: FONT.mono, fontSize: px(11), color: PALETTE.ink })
    .setOrigin(0.5);

  const width = text.width + 24;
  const height = 24;
  const frame = scene.add.rectangle(0, 0, width, height, PALETTE.abyss, 0.8);
  frame.setStrokeStyle(1, PALETTE.panelEdge);

  const button = scene.add.container(right - width / 2, bottom - height / 2, [frame, text]);
  button.setSize(width, height);
  button.setInteractive(
    new Phaser.Geom.Rectangle(0, 0, width, height),
    Phaser.Geom.Rectangle.Contains,
  );
  button.input!.cursor = 'pointer';
  button.on('pointerover', () => frame.setStrokeStyle(1, PALETTE.coast));
  button.on('pointerout', () => frame.setStrokeStyle(1, PALETTE.panelEdge));
  button.on('pointerdown', () => {
    audio.resume();
    audio.cue('select');
    onClick();
  });

  return button;
}
